import { useEffect, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  TRACKER_ACTIVE_SURFACE_SX,
  TRACKER_DASHED_TILE_SX,
  TRACKER_FIELD_SX,
  TRACKER_GOING_TONE,
  TRACKER_PILL_SX,
  GUEST_PRIMARY_BUTTON_SX,
  GUEST_TEXT_BUTTON_SX,
} from './guestTableStyles';
import GuestLinksTile, { type NewGuestLink } from './GuestLinksTile';
import GuestTagsTile from './GuestTagsTile';
import RecordSection, { TILE_FLEX } from './RecordSection';
import {
  TRACKER_BOOKING_SOURCE_LABELS,
  TRACKER_GOING_LABELS,
  type GuestLink,
  type GuestTag,
  type TrackerBookingSource,
  type TrackerGuest,
  type TrackerPatch,
  type TrackerSection,
} from '../../../services/tracker-api-client';
import {
  formatEditedBy,
  guestGoingState,
  toDateInputValue,
} from '../../../lib/trackerPresentation';
import type { CreateTagKind } from '../../../lib/guestTags';

const BOOKING_SOURCES: TrackerBookingSource[] = ['tripsby', 'guest'];

/**
 * Every custom section renders this field explicitly, so it must be kept out of
 * the mapped list — otherwise the first saved note draws the field a second
 * time, and the two copies fight over the same value.
 */
const RESERVED_NOTE_FIELD = 'Note';

function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** A labelled field that commits on blur, exactly like the inline table cells. */
function EditorField({
  label,
  value,
  type = 'text',
  multiline = false,
  helperText,
  onCommit,
}: {
  label: string;
  value: string;
  type?: 'text' | 'date' | 'email' | 'tel' | 'number';
  multiline?: boolean;
  helperText?: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(value);
  }, [value, focused]);

  return (
    <TextField
      fullWidth
      size="small"
      label={label}
      type={type}
      value={draft}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      helperText={helperText}
      onChange={(event) => setDraft(event.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (draft !== value) onCommit(draft);
      }}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      sx={TRACKER_FIELD_SX}
    />
  );
}

export interface GuestRecordEditorProps {
  guest: TrackerGuest;
  sections: TrackerSection[];
  onPatch: (inviteeId: string, patch: TrackerPatch) => void;
  onClose: () => void;
  onCreateSection: (label: string) => void;
  creatingSection: boolean;
  /**
   * The tag vocabulary, or null when this API has no tags. Null drops the tag
   * and link tiles entirely — the record is honest about what it can store
   * rather than offering wells that swallow what a host types.
   */
  vocabulary: GuestTag[] | null;
  links: GuestLink[];
  /** Everyone on the trip, for the link picker. No server search — see GuestLinksTile. */
  roster: TrackerGuest[];
  tagBusy: boolean;
  /**
   * The last tag or link refusal, in the host's words.
   *
   * Three write paths fire from this record — remove a tag, link two guests,
   * unlink them — and every one of them was refused into a state nothing
   * mounted could render: the chip stayed where it was, the link form folded
   * away, and the host clicked again. A write surface with no error surface is
   * a control that silently does nothing, which is the failure this whole tab
   * is written against.
   */
  tagError: string;
  onDismissTagError: () => void;
  /** The intent the palette opens with — see `TagPaletteProps.defaultKind`. */
  onOpenTagPalette: (intent: CreateTagKind) => void;
  onRemoveTag: (tagId: string) => void;
  /** Resolves false when the write was refused, so the form keeps what was typed. */
  onCreateLink: (link: NewGuestLink) => Promise<boolean>;
  onDeleteLink: (linkId: string) => void;
}

/**
 * The full record for one guest, rendered BELOW the table rather than in a
 * drawer over it: a host cross-checks the row against the record while typing,
 * and an overlay hides exactly the row they are working on.
 */
export function GuestRecordEditor({
  guest,
  sections,
  onPatch,
  onClose,
  onCreateSection,
  creatingSection,
  vocabulary,
  links,
  roster,
  tagBusy,
  tagError,
  onDismissTagError,
  onOpenTagPalette,
  onRemoveTag,
  onCreateLink,
  onDeleteLink,
}: GuestRecordEditorProps) {
  const [newSectionLabel, setNewSectionLabel] = useState('');
  const [addingSection, setAddingSection] = useState(false);
  const edited = formatEditedBy(guest.updatedBy, guest.updatedAt);
  const going = guestGoingState(guest);
  const goingTone = TRACKER_GOING_TONE[going];

  // A new selection is a different record: never carry a half-typed section
  // name from the previous guest, and never carry a refusal about the last one
  // either. "Another host linked them already" is about two named people, and
  // read under a third person's name it is worse than no message at all.
  useEffect(() => {
    setNewSectionLabel('');
    setAddingSection(false);
    onDismissTagError();
    // Only when the record changes: adding onDismissTagError would re-run this
    // on every render of the tab above, wiping an error as it arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guest.inviteeId]);

  const patch = (next: TrackerPatch) => onPatch(guest.inviteeId, next);
  const customFields = guest.customFields ?? {};

  return (
    <Paper
      component="section"
      aria-label={`Record for ${guest.fullName}`}
      elevation={0}
      // The open record is marked by a green edge alone — a bordered card never
      // also carries a shadow.
      sx={{ ...TRACKER_ACTIVE_SURFACE_SX, p: { xs: 2, sm: 2.5 } }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" spacing={1.25}>
            <Typography
              component="h2"
              sx={{
                color: 'var(--tsb-fg)',
                fontSize: { xs: 15, sm: 16 },
                fontWeight: 700,
                lineHeight: 1.3,
                overflowWrap: 'anywhere',
              }}
            >
              {guest.fullName}
            </Typography>
            <Box sx={{ ...TRACKER_PILL_SX, bgcolor: goingTone.bg, color: goingTone.fg }}>
              {TRACKER_GOING_LABELS[going]}
            </Box>
          </Stack>
          <Typography sx={{ mt: 0.5, color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
            {[guest.invitedBy ? `Invited by ${guest.invitedBy}` : null, edited]
              .filter(Boolean)
              .join(' · ') || 'Everything here saves as you type.'}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close this record"
          sx={{
            flexShrink: 0,
            borderRadius: 'var(--tsb-radius-pill)',
            color: 'var(--tsb-fg-muted)',
            '&:hover': { bgcolor: 'var(--tsb-bg-elevated)', color: 'var(--tsb-fg)' },
            '&.Mui-focusVisible': { boxShadow: 'var(--tsb-focus-ring)' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5, mt: 2.5 }}>
        <RecordSection
          title="Contact"
          description="Writes straight to the invitee record — the address invites and broadcasts go to."
        >
          <Stack spacing={1.5}>
            <EditorField
              label="Email"
              type="email"
              value={guest.email ?? ''}
              onCommit={(next) => patch({ contactEmail: orNull(next) })}
            />
            <EditorField
              label="Phone"
              type="tel"
              value={guest.phone ?? ''}
              onCommit={(next) => patch({ contactPhone: orNull(next) })}
            />
          </Stack>
        </RecordSection>

        <RecordSection title="Travel" description="When they land and when they leave.">
          <Stack spacing={1.5}>
            {/* A native date input has a stubborn intrinsic width, so the pair
                gets flex bases and wraps rather than overflowing the tile. */}
            <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 1.5 }}>
              <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
                <EditorField
                  label="Arrives"
                  type="date"
                  value={toDateInputValue(guest.arrivalDate)}
                  onCommit={(next) => patch({ arrivalDate: orNull(next) })}
                />
              </Box>
              <Box sx={{ flex: '1 1 120px', minWidth: 0 }}>
                <EditorField
                  label="Departs"
                  type="date"
                  value={toDateInputValue(guest.departureDate)}
                  onCommit={(next) => patch({ departureDate: orNull(next) })}
                />
              </Box>
            </Stack>
            <EditorField
              label="Party size"
              type="number"
              value={guest.partySize == null ? '' : String(guest.partySize)}
              onCommit={(next) => {
                const trimmed = next.trim();
                if (!trimmed) return patch({ partySize: null });
                // A non-numeric value serializes to null, which clears the
                // field — drop it instead of wiping what is already stored.
                const parsed = Number(trimmed);
                if (Number.isFinite(parsed)) patch({ partySize: parsed });
              }}
            />
          </Stack>
        </RecordSection>

        <RecordSection
          title="Stay"
          description="Room and confirmation number stay on this host-only page — they are never rendered on a share card or a preview."
        >
          <Stack spacing={1.5}>
            <EditorField
              label="Staying at"
              value={guest.stayingAt ?? ''}
              onCommit={(next) => patch({ stayingAt: orNull(next) })}
            />
            <TextField
              select
              fullWidth
              size="small"
              label="Booked via"
              value={guest.bookingSource ?? ''}
              helperText="Automatic for Tripsby bookings — set “by guest” for outside reservations."
              onChange={(event) =>
                patch({
                  bookingSource: (event.target.value || null) as TrackerBookingSource | null,
                })
              }
              sx={TRACKER_FIELD_SX}
            >
              <MenuItem value="">Unknown</MenuItem>
              {BOOKING_SOURCES.map((source) => (
                <MenuItem key={source} value={source}>
                  {TRACKER_BOOKING_SOURCE_LABELS[source]}
                </MenuItem>
              ))}
            </TextField>
            <EditorField
              label="Room + confirmation number"
              value={guest.roomDetails ?? ''}
              helperText="For example: Room 412 · confirmation E2E-TEST-002"
              onCommit={(next) => patch({ roomDetails: orNull(next) })}
            />
          </Stack>
        </RecordSection>

        {vocabulary ? (
          <>
            {/*
              One reader for all three write paths below. Full-width inside the
              wrapping tile row so it sits above the pair it explains rather
              than becoming a third column.
            */}
            {tagError ? (
              <Alert severity="error" onClose={onDismissTagError} sx={{ flex: '1 1 100%' }}>
                {tagError}
              </Alert>
            ) : null}
            <GuestTagsTile
              guest={guest}
              vocabulary={vocabulary}
              busy={tagBusy}
              onOpenPalette={onOpenTagPalette}
              onRemove={onRemoveTag}
            />
            <GuestLinksTile
              guest={guest}
              roster={roster}
              links={links}
              busy={tagBusy}
              onCreate={onCreateLink}
              onDelete={onDeleteLink}
            />
          </>
        ) : null}

        <RecordSection title="Preferences & notes" description="Anything the hosts need to remember.">
          <EditorField
            label="Notes"
            multiline
            value={guest.preferencesNotes ?? ''}
            onCommit={(next) => patch({ preferencesNotes: orNull(next) })}
          />
        </RecordSection>

        {sections.map((section) => {
          const values = customFields[section.sectionKey] ?? {};
          const labels = Object.keys(values).filter((key) => key !== RESERVED_NOTE_FIELD);
          return (
            <RecordSection key={section.id} title={section.label}>
              <Stack spacing={1.5}>
                {labels.map((fieldLabel) => (
                  <EditorField
                    key={fieldLabel}
                    label={fieldLabel}
                    value={values[fieldLabel] ?? ''}
                    onCommit={(next) =>
                      patch({
                        customFields: { [section.sectionKey]: { [fieldLabel]: orNull(next) } },
                      })
                    }
                  />
                ))}
                <EditorField
                  label={RESERVED_NOTE_FIELD}
                  value={values[RESERVED_NOTE_FIELD] ?? ''}
                  helperText={labels.length ? undefined : 'Whatever you need to track here.'}
                  onCommit={(next) =>
                    patch({
                      customFields: {
                        [section.sectionKey]: { [RESERVED_NOTE_FIELD]: orNull(next) },
                      },
                    })
                  }
                />
              </Stack>
            </RecordSection>
          );
        })}

        {addingSection ? (
          <Box sx={{ ...TRACKER_DASHED_TILE_SX, flex: TILE_FLEX }}>
            <Stack spacing={1.5}>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="What are you tracking?"
                value={newSectionLabel}
                onChange={(event) => setNewSectionLabel(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && newSectionLabel.trim()) {
                    onCreateSection(newSectionLabel.trim());
                    setNewSectionLabel('');
                    setAddingSection(false);
                  }
                }}
                sx={TRACKER_FIELD_SX}
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  disabled={!newSectionLabel.trim() || creatingSection}
                  onClick={() => {
                    onCreateSection(newSectionLabel.trim());
                    setNewSectionLabel('');
                    setAddingSection(false);
                  }}
                  sx={GUEST_PRIMARY_BUTTON_SX}
                >
                  {creatingSection ? 'Adding…' : 'Add section'}
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setAddingSection(false);
                    setNewSectionLabel('');
                  }}
                  sx={{ ...GUEST_TEXT_BUTTON_SX, color: 'var(--tsb-fg-muted)' }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={() => setAddingSection(true)}
            sx={{
              ...TRACKER_DASHED_TILE_SX,
              flex: TILE_FLEX,
              alignSelf: 'stretch',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 0.75,
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'var(--tsb-font-sans)',
              '&:hover': { borderColor: 'var(--tsb-brand-border)' },
              '&:focus-visible': {
                borderColor: 'var(--tsb-brand-border)',
                outline: '2px solid var(--tsb-brand-hover)',
                outlineOffset: 2,
              },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <AddRoundedIcon sx={{ fontSize: 17, color: 'var(--tsb-brand-ink)' }} />
              <Typography sx={{ color: 'var(--tsb-brand-ink)', fontSize: 13, fontWeight: 600 }}>
                Track something new
              </Typography>
            </Stack>
            <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5, lineHeight: 1.5 }}>
              Add a section for every guest — flights, transfers, kids&rsquo; club… New Tripsby
              services add their own section here automatically.
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

export default GuestRecordEditor;
